import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../pages-styles/project-consultancy.css';
import { usePageMeta } from '../../hooks/usePageMeta';

export default function ICTConsultancy() {
  usePageMeta(
    'ICT & Digital Project Consultancy - Magbity Agency Ltd',
    'Professional ICT services including software troubleshooting, hardware repair, digital transformation, and technology project consultancy.'
  );

  useEffect(() => {
    const el = document.querySelector('.ict-3d-card');
    if (el) el.classList.add('enter');
  }, []);

  return (
    <div className="project-consultancy">
      <section className="consultancy-hero-banner ict-hero">
        <div className="consultancy-hero-overlay"></div>
        <div className="consultancy-hero-content">
          <div className="consultancy-hero-text">
            <h1>ICT & Digital Project Consultancy</h1>
            <p>
              Comprehensive digital transformation and technology solutions. From software troubleshooting 
              and hardware maintenance to digital project strategy and system architecture, we provide expert 
              ICT consultancy tailored to your organization's needs.
            </p>
          </div>

          <div className="ict-illustration">
            <div className="ict-3d-scene">
              <div className="ict-3d-card">
                <div className="face front">
                  <svg width="220" height="220" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="ictGrad" x1="0" x2="1">
                        <stop offset="0%" stopColor="#16c784" />
                        <stop offset="100%" stopColor="#00d4ff" />
                      </linearGradient>
                    </defs>
                    <rect x="10" y="10" width="200" height="200" rx="16" fill="url(#ictGrad)" opacity="0.12" />
                    <g transform="translate(30,30) scale(0.95)">
                      <rect x="10" y="20" width="110" height="80" rx="6" fill="none" stroke="#fff" strokeWidth="3" opacity="0.15" />
                      <circle cx="35" cy="45" r="6" fill="#fff" opacity="0.12" />
                      <circle cx="65" cy="45" r="6" fill="#fff" opacity="0.12" />
                      <circle cx="95" cy="45" r="6" fill="#fff" opacity="0.12" />
                      <rect x="15" y="75" width="100" height="3" rx="1.5" fill="#fff" opacity="0.1" />
                      <rect x="15" y="85" width="100" height="3" rx="1.5" fill="#fff" opacity="0.1" />
                    </g>
                  </svg>
                  <div className="card-label">Digital Solutions</div>
                </div>
                <div className="face back">
                  <h4>Technology Excellence</h4>
                  <p>Software solutions, hardware optimization, and digital transformation services.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="consultancy-services ict-details">
        <h2>ICT & Digital Services</h2>
        <div className="services-list">
          <div className="service-item left">
            <div className="service-content">
              <h3>Software Troubleshooting & Support</h3>
              <p>
                Expert diagnosis and resolution of software issues across operating systems, applications, and 
                networks. We provide both on-site support for critical issues and remote troubleshooting for 
                faster resolution.
              </p>
              <ul className="service-details-list">
                <li>Operating system optimization and virus/malware removal</li>
                <li>Application installation, configuration, and performance tuning</li>
                <li>On-site technical support for critical failures</li>
                <li>Remote support for rapid issue diagnosis and resolution</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" opacity=".2"/>
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5-9h10v2H7z" opacity=".2"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5-9h10v2H7z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Hardware Repair & Maintenance</h3>
              <p>
                Preventive and corrective hardware maintenance including diagnostics, repairs, component replacement, 
                and performance optimization for computers, servers, and peripherals.
              </p>
              <ul className="service-details-list">
                <li>Hardware diagnostics and component testing</li>
                <li>Equipment repair and component replacement</li>
                <li>Preventive maintenance and system upgrades</li>
                <li>Warranty coordination and spare parts management</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Network Design & Infrastructure Planning</h3>
              <p>
                Design robust network architectures, plan ICT infrastructure, and implement security protocols 
                to support organizational growth and protect critical systems.
              </p>
              <ul className="service-details-list">
                <li>Network design and infrastructure planning</li>
                <li>Security assessment and implementation</li>
                <li>Server and storage solutions</li>
                <li>Cloud migration and hybrid infrastructure</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 13H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zM7 19c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM20 3H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1zM7 11c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" opacity=".2"/>
                <path d="M20 13H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zM7 19c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM20 3H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1zM7 11c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" opacity=".2"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Digital Project Consultancy & Strategy</h3>
              <p>
                Develop digital transformation strategies, manage technology projects, and guide organizations 
                through digital adoption with change management and training support.
              </p>
              <ul className="service-details-list">
                <li>Digital transformation strategy and roadmap</li>
                <li>Technology project management and oversight</li>
                <li>Change management and organizational adoption</li>
                <li>Staff training and knowledge transfer</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>System Architecture & Technology Advisory</h3>
              <p>
                Provide strategic technology advisory, design scalable system architectures, and recommend 
                technology stacks that align with business objectives and growth potential.
              </p>
              <ul className="service-details-list">
                <li>Technology assessment and recommendation</li>
                <li>System architecture design and optimization</li>
                <li>Technology roadmap and procurement planning</li>
                <li>Vendor evaluation and partnership management</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9h18V5H3v4zm0 5h18v-4H3v4zm0 5h18v-4H3v4z" opacity=".2"/>
                <path d="M3 9h18V5H3v4zm0 5h18v-4H3v4zm0 5h18v-4H3v4z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" opacity=".2"/>
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>IT Support & Help Desk Services</h3>
              <p>
                Deliver comprehensive IT support services including help desk operations, incident management, 
                user training, and documentation to maximize technology ROI.
              </p>
              <ul className="service-details-list">
                <li>24/7 help desk and incident response</li>
                <li>User technical training and support</li>
                <li>System documentation and procedures</li>
                <li>Performance monitoring and optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="ict-implementation">
        <h2>ICT Project Implementation Cycle</h2>
        <div className="process-steps">
          <div className="step">
            <div className="step-number">1</div>
            <h4>Assessment</h4>
            <p>Evaluate current ICT infrastructure and identify gaps</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h4>Planning</h4>
            <p>Develop technology strategy and implementation plan</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h4>Design</h4>
            <p>Design system architecture and technical specifications</p>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <h4>Implementation</h4>
            <p>Deploy solutions and integrate with existing systems</p>
          </div>
          <div className="step">
            <div className="step-number">5</div>
            <h4>Testing</h4>
            <p>Comprehensive testing and quality assurance</p>
          </div>
          <div className="step">
            <div className="step-number">6</div>
            <h4>Support</h4>
            <p>Training, documentation, and ongoing support</p>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <h2>Ready to Transform Your Digital Infrastructure?</h2>
        <p>Let's discuss how we can optimize your ICT systems and accelerate digital transformation.</p>
        <Link to="/contact" className="cta-button">Schedule ICT Consultation</Link>
      </section>
    </div>
  );
}
